import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { adminAPI } from '../services/api';
import { retryAfterSeconds } from '../utils/retryAfter';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || `${window.location.origin}/api`;


const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [subscription, setSubscription] = useState(null);

  // Configure axios to send credentials
  axios.defaults.withCredentials = true;
  axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

  // Check if user is logged in on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async (retried = false) => {
    try {
      setAuthError(null);

      // Add timeout to prevent infinite loading
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Authentication timeout')), 10000)
      );

      const response = await Promise.race([
        axios.get(`${API_BASE_URL}/auth/me`, { withCredentials: true }),
        timeoutPromise
      ]);

      setUser(response.data.user);
      setAuthError(null);

      // Super admins have no subscription (and the customer API refuses them)
      if (response.data.user?.role === 'super_admin') {
        setSubscription(null);
        return;
      }

      // Fetch subscription status now that we know the user is logged in
      try {
        const subRes = await axios.get(`${API_BASE_URL}/billing/subscription`);
        setSubscription(subRes.data.has_subscription ? subRes.data.subscription : null);
      } catch {
        setSubscription(null);
      }

    } catch (error) {
      // 401 is expected when not logged in - don't treat it as an error
      const serverMessage = error.response?.data?.error;
      if (
        error.response?.status === 401 && !retried &&
        (serverMessage === 'View-as session expired' || serverMessage === 'View-as session ended' ||
         serverMessage === 'The customer account is disabled')
      ) {
        // The server already restored the operator session: ask again instead of showing login
        return checkAuth(true);
      }
      if (error.response?.status === 401) {
        setUser(null);
        setSubscription(null);
        setAuthError(null); // Clear any previous errors
      } else if (error.message === 'Authentication timeout') {
        console.error('Auth check timeout:', error);
        setAuthError('Authentication service is unavailable. Please check your connection and try again.');
        setUser(null);
      } else {
        console.error('Auth check failed:', error);
        setAuthError('Authentication check failed. Please try logging in again.');
        setUser(null);
        setSubscription(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const retryAuth = () => {
    setLoading(true);
    setAuthError(null);
    checkAuth();
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/login`, {
        email,
        password,
        remember: true
      }, { withCredentials: true });
      setUser(response.data.user);
      if (response.data.user?.role === 'super_admin') {
        setSubscription(null);
        return response.data;
      }
      // Fetch subscription immediately so PrivateRoute can make the right redirect
      try {
        const subRes = await axios.get(`${API_BASE_URL}/billing/subscription`);
        setSubscription(subRes.data.has_subscription ? subRes.data.subscription : null);
      } catch {
        setSubscription(null);
      }
      return response.data;
    } catch (error) {
      // Carry the HTTP status (and the wait for a 429) so Login can show a countdown
      const err = new Error(error.response?.data?.error || 'Login failed');
      err.status = error.response?.status;
      if (err.status === 429) err.retryAfter = retryAfterSeconds(error);
      throw err;
    }
  };

  const register = async (email, password, name) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/register`, {
        email,
        password,
        name
      }, { withCredentials: true });
      setUser(response.data.user);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Registration failed');
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_BASE_URL}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setSubscription(null);
    }
  };

  const refreshSubscription = async () => {
    try {
      const subRes = await axios.get(`${API_BASE_URL}/billing/subscription`);
      setSubscription(subRes.data.has_subscription ? subRes.data.subscription : null);
    } catch {
      setSubscription(null);
    }
  };

  // Re-read /auth/me (e.g. after the server restored the operator session)
  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/auth/me`, { withCredentials: true });
      setUser(response.data.user);
      return response.data.user;
    } catch (error) {
      if (error.response?.status === 401) {
        setUser(null);
        setSubscription(null);
      }
      return null;
    }
  };

  // View-as: a full page load afterwards so nothing cached from the other identity survives
  const startViewAs = async (customerId) => {
    await adminAPI.viewAsCustomer(customerId);
    window.location.assign('/dashboard');
  };

  const exitViewAs = async () => {
    await adminAPI.exitViewAs();
    window.location.assign('/admin');
  };

  const value = {
    user,
    role: user?.role || null,
    isSuperAdmin: user?.role === 'super_admin',
    impersonating: user?.impersonating || null,
    refreshUser,
    startViewAs,
    exitViewAs,
    login,
    register,
    logout,
    loading,
    authError,
    retryAuth,
    isAuthenticated: !!user,
    subscription,
    hasActiveSubscription: subscription?.status === 'active' || subscription?.status === 'past_due',
    refreshSubscription,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
